/**
 * Centralized description map for every internal URL that may appear in a
 * SpokePage's `relatedLinks`. Used by SpokePage to auto-fill the
 * RelatedPagesGrid card description so every spoke page renders accurate,
 * differentiated card copy without forcing every route file to repeat it.
 *
 * Keep `relatedLinks` entries in route files focused on VARIED anchor text
 * (the `label`, per Rule #5). Descriptions resolve here automatically.
 *
 * If a `to` is missing from this map, SpokePage falls back to a generic
 * sentence — add the URL here so the card reads naturally.
 */

import {
  SERVICE_HUBS,
  CITY_HUBS,
  INDUSTRY_HUBS,
  CS_SPOKES,
  HVAC_SPOKES,
  PLUMBING_SPOKES,
  ROOFING_SPOKES,
  ELECTRICAL_SPOKES,
  LAW_FIRM_SPOKES,
  DENTAL_SPOKES,
} from "@/lib/hubLinkData";

/** Compose a flat to → description map from every catalog entry. */
function buildCatalogDescriptions(): Record<string, string> {
  const out: Record<string, string> = {};
  const sources = [
    SERVICE_HUBS,
    CITY_HUBS,
    INDUSTRY_HUBS,
    CS_SPOKES,
    HVAC_SPOKES,
    PLUMBING_SPOKES,
    ROOFING_SPOKES,
    ELECTRICAL_SPOKES,
    LAW_FIRM_SPOKES,
    DENTAL_SPOKES,
  ];
  for (const src of sources) {
    for (const entry of Object.values(src)) {
      if (!out[entry.to]) out[entry.to] = entry.description;
    }
  }
  return out;
}

/**
 * Manual descriptions for trust / utility / blog / intersection routes
 * that aren't in the hub catalog.
 */
const EXTRA_DESCRIPTIONS: Record<string, string> = {
  // Trust pages
  "/case-studies":
    "Real Colorado wins — ranking, lead, and revenue results from companies like yours.",
  "/reviews":
    "Verified reviews from Colorado business owners we work with day-to-day.",
  "/meet-chris":
    "Meet Chris — the owner-operator behind every Colorado Web Impressions engagement.",

  // Utility pages
  "/pricing":
    "Public, transparent pricing for SEO, web, ads, and AI search — no contracts, no surprises.",
  "/contact":
    "Talk to the owner directly — free 20-minute Colorado strategy call, no sales team.",
  "/faq":
    "Straight answers on pricing, timelines, contracts, reporting, and what we actually do.",
  "/are-we-a-fit": "Honest gut-check on whether we're the right Colorado partner for your business.",
  "/free-audit": "Free Colorado SEO + site audit — see exactly where you're losing leads.",

  // Service-area utility
  "/service-areas": "Every Colorado city and region we actively serve — front range to western slope.",

  // Industries index
  "/industries": "Industries we specialize in across Colorado — trades, healthcare, legal, and more.",

  // Blog index + posts
  "/learn": "Plain-English Colorado SEO, web, and AI-search guides — written by a practitioner.",
  "/learn/google-ads-vs-seo-colorado":
    "Where Google Ads beats SEO (and vice versa) for Colorado service businesses in 2026.",

  // Tier 7 intersection pages — auto-generated short copy
  "/hvac-seo-denver": "Dedicated SEO playbook for Denver HVAC companies.",
  "/hvac-seo-colorado-springs": "Dedicated SEO playbook for Colorado Springs HVAC companies.",
  "/hvac-seo-fort-collins": "Dedicated SEO playbook for Fort Collins HVAC companies.",
  "/hvac-seo-boulder": "Dedicated SEO playbook for Boulder HVAC companies.",
  "/plumbing-seo-denver": "Dedicated SEO playbook for Denver plumbing companies.",
  "/plumbing-seo-colorado-springs": "Dedicated SEO playbook for Colorado Springs plumbers.",
  "/plumbing-seo-fort-collins": "Dedicated SEO playbook for Fort Collins plumbers.",
  "/plumbing-seo-boulder": "Dedicated SEO playbook for Boulder plumbing companies.",
  "/roofing-seo-denver": "Storm-season SEO playbook for Denver roofers.",
  "/roofing-seo-colorado-springs": "Storm-season SEO playbook for Colorado Springs roofers.",
  "/roofing-seo-fort-collins": "Storm-season SEO playbook for Fort Collins roofers.",
  "/roofing-seo-boulder": "Storm-season SEO playbook for Boulder roofers.",
  "/electrical-seo-denver": "Dedicated SEO playbook for Denver electrical contractors.",
  "/electrical-seo-colorado-springs":
    "Dedicated SEO playbook for Colorado Springs electrical contractors.",
  "/electrical-seo-fort-collins": "Dedicated SEO playbook for Fort Collins electricians.",
  "/electrical-seo-boulder": "Dedicated SEO playbook for Boulder electricians.",
  "/dental-seo-denver": "New-patient SEO playbook for Denver dental practices.",
  "/dental-seo-colorado-springs": "New-patient SEO playbook for Colorado Springs dentists.",
  "/dental-seo-fort-collins": "New-patient SEO playbook for Fort Collins dentists.",
  "/dental-seo-boulder": "New-patient SEO playbook for Boulder dental practices.",
  "/law-firm-seo-denver": "Practice-area SEO playbook for Denver law firms.",
  "/law-firm-seo-colorado-springs": "Practice-area SEO playbook for Colorado Springs attorneys.",
  "/law-firm-seo-fort-collins": "Practice-area SEO playbook for Fort Collins attorneys.",
  "/law-firm-seo-boulder": "Practice-area SEO playbook for Boulder law firms.",
  "/landscaping-seo-denver": "Seasonal SEO playbook for Denver landscape companies.",
  "/landscaping-seo-colorado-springs":
    "Seasonal SEO playbook for Colorado Springs landscapers.",
  "/landscaping-seo-fort-collins": "Seasonal SEO playbook for Fort Collins landscapers.",
  "/landscaping-seo-boulder": "Seasonal SEO playbook for Boulder landscape companies.",
  "/contractor-seo-denver": "SEO playbook for Denver general and specialty contractors.",
  "/contractor-seo-colorado-springs":
    "SEO playbook for Colorado Springs general contractors.",
  "/contractor-seo-fort-collins": "SEO playbook for Fort Collins contractors.",
  "/contractor-seo-boulder": "SEO playbook for Boulder contractors.",
};

const CATALOG = buildCatalogDescriptions();

/**
 * Resolve a description for an internal `to`. Returns undefined if neither
 * the catalog nor the extra map has an entry — caller should fall back.
 */
export function describeRoute(to: string): string | undefined {
  return CATALOG[to] ?? EXTRA_DESCRIPTIONS[to];
}
